import { useState, useEffect, useRef, useMemo } from "react";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Select,
  SelectItem,
} from "@heroui/react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { emailEditorModules } from "shared/config/quillSetup";
import { DotsSixVertical, Plus, Database, UsersThree } from "@phosphor-icons/react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { useSession } from "@clerk/clerk-react";
import { toast } from "react-toastify";
import { EmailTemplate, TemplateType, EmailSettings } from "./types";

interface EditTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  template: EmailTemplate | null;
  templateTypes: TemplateType[];
  emailSettings: EmailSettings | null;
  onSuccess: () => void;
}

const modules = {
  ...emailEditorModules,
};

export const EditTemplateModal: React.FC<EditTemplateModalProps> = ({
  isOpen,
  onClose,
  template,
  templateTypes,
  emailSettings,
  onSuccess
}) => {
  const { session } = useSession();
  const [templateSubject, setTemplateSubject] = useState("");
  const [templateContent, setTemplateContent] = useState("");
  const [templateType, setTemplateType] = useState("");
  const [templateLabel, setTemplateLabel] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Entity management state
  const [showAddEntity, setShowAddEntity] = useState(false);
  const [selectedEntities, setSelectedEntities] = useState<any[]>([]);
  
  // Parameter management state
  const [parameters, setParameters] = useState<Array<{
    id: string;
    name: string;
    placeholder: string;
    type: 'global' | 'individual';
  }>>([]);
  const [showCreateParameter, setShowCreateParameter] = useState(false);
  const [newParameterName, setNewParameterName] = useState('');
  const [newParameterType, setNewParameterType] = useState<'global' | 'individual'>('global');
  const [enabledFields, setEnabledFields] = useState<any>({});
  const [fieldsLoading, setFieldsLoading] = useState(false);

  // Refs for drag and drop
  const quillRef = useRef<ReactQuill>(null);
  const subjectInputRef = useRef<HTMLInputElement>(null);

  // Dummy contact data for preview
  const dummyContact = {
    id: "dummy",
    name: "Jan Janssen (voorbeeld)",
    resourceId: "RES-2024-001", 
    firstName: "Jan",
    lastName: "Janssen",
    phoneMobile: "+32 123 456 789",
    email: "jan.janssen@example.be"
  };

  // Function to replace dynamic values in preview content
  const replacePreviewDynamicValues = (content: string, isHtml: boolean = true): string => {
    let personalizedContent = content;
    
    // Available dynamic values - Dutch placeholders (Contact fields)
    const contactDynamicValues = {
      '{ResourceID}': dummyContact.resourceId,
      '{VolledigeNaam}': dummyContact.name,
      '{Voornaam}': dummyContact.firstName,
      '{Achternaam}': dummyContact.lastName,
      '{MobielNummer}': dummyContact.phoneMobile,
      '{Email}': dummyContact.email
    };

    // Replace contact dynamic values
    Object.entries(contactDynamicValues).forEach(([placeholder, value]) => {
      if (isHtml) {
        // For HTML content, add highlighting with contact styling
        const highlightedValue = `<span class="bg-yellow-100 px-1 py-0.5 rounded-sm text-xs font-medium border border-yellow-300" title="Contact veld: ${placeholder}">${value}</span>`;
        personalizedContent = personalizedContent.replace(new RegExp(placeholder.replace(/[{}]/g, '\\$&'), 'g'), highlightedValue);
      } else {
        // For plain text (like subject), just replace with value
        personalizedContent = personalizedContent.replace(new RegExp(placeholder.replace(/[{}]/g, '\\$&'), 'g'), value || '');
      }
    });

    // Replace entity dynamic values
    const entityFieldGroups = getEntityDynamicFields;
    Object.values(entityFieldGroups).forEach((entityGroup: any) => {
      entityGroup.fields.forEach((field: any) => {
        const placeholder = field.placeholder;
        const value = field.value;
        
        if (isHtml) {
          // For HTML content, add highlighting with entity styling
          const highlightedValue = `<span class="bg-green-100 px-1 py-0.5 rounded-sm text-xs font-medium border border-green-300" title="Entiteit veld: ${placeholder} (${field.entityName})">${value}</span>`;
          personalizedContent = personalizedContent.replace(new RegExp(placeholder.replace(/[{}]/g, '\\$&'), 'g'), highlightedValue);
        } else {
          // For plain text (like subject), just replace with value
          personalizedContent = personalizedContent.replace(new RegExp(placeholder.replace(/[{}]/g, '\\$&'), 'g'), value || '');
        }
      });
    });

    // Replace parameter dynamic values (supporting new gl_/ind_ format)
    parameters.forEach((parameter) => {
      const placeholder = parameter.placeholder;
      const parameterValue = `[Voorbeeld ${parameter.name}]`; // Dummy value for template preview
      
      if (isHtml) {
        // For HTML content, add highlighting with parameter styling
        const titleText = `Variabel veld: ${placeholder} (${parameter.type === 'global' ? 'Globaal' : 'Individueel'})`.replace(/"/g, '&quot;');
        const highlightedValue = `<span class="bg-purple-100 px-1 py-0.5 rounded-sm text-xs font-medium border border-purple-300" title="${titleText}">${parameterValue}</span>`;
        personalizedContent = personalizedContent.replace(new RegExp(placeholder.replace(/[{}]/g, '\\$&'), 'g'), highlightedValue);
      } else {
        // For plain text (like subject), just replace with value
        personalizedContent = personalizedContent.replace(new RegExp(placeholder.replace(/[{}]/g, '\\$&'), 'g'), parameterValue);
      }
    });

    return personalizedContent;
  };

  // Dynamic fields data for drag and drop
  const dynamicFields = [
    { key: 'firstName', label: 'Voornaam', placeholder: '{Voornaam}', value: dummyContact.firstName },
    { key: 'lastName', label: 'Achternaam', placeholder: '{Achternaam}', value: dummyContact.lastName },
    { key: 'name', label: 'Volledige naam', placeholder: '{VolledigeNaam}', value: dummyContact.name },
    { key: 'email', label: 'E-mailadres', placeholder: '{Email}', value: dummyContact.email },
    { key: 'phoneMobile', label: 'Mobiel nummer', placeholder: '{MobielNummer}', value: dummyContact.phoneMobile },
    { key: 'resourceId', label: 'Resource ID', placeholder: '{ResourceID}', value: dummyContact.resourceId }
  ];

  // Handle drag start
  const handleDragStart = (e: React.DragEvent, placeholder: string) => {
    e.dataTransfer.setData('text/plain', placeholder);
    e.dataTransfer.effectAllowed = 'copy';
  };

  // Handle drop on subject input
  const handleSubjectDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const placeholder = e.dataTransfer.getData('text/plain');
    if (placeholder && subjectInputRef.current) {
      const input = subjectInputRef.current;
      const start = input.selectionStart || 0;
      const end = input.selectionEnd || 0;
      const value = templateSubject;
      const newValue = value.substring(0, start) + placeholder + value.substring(end);
      setTemplateSubject(newValue);
      
      // Set cursor position after the inserted text
      setTimeout(() => {
        input.focus();
        input.setSelectionRange(start + placeholder.length, start + placeholder.length);
      }, 0);
    }
  };

  // Handle drop on ReactQuill editor
  const handleEditorDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const placeholder = e.dataTransfer.getData('text/plain');
    if (placeholder && quillRef.current) {
      const editor = quillRef.current.getEditor();
      
      // Get the exact position from drop coordinates
      const editorEl = editor.root;
      const rect = editorEl.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      
      // Try to find the exact position using browser APIs
      let insertIndex = editor.getLength() - 1; // fallback to end
      
      try {
        // Create a temporary range at the drop coordinates
        // Use modern caretPositionFromPoint or fallback to deprecated caretRangeFromPoint
        if ((document as any).caretPositionFromPoint) {
          // Modern API (Firefox)
          const caretPos = (document as any).caretPositionFromPoint(e.clientX, e.clientY);
          if (caretPos && caretPos.offsetNode && editorEl.contains(caretPos.offsetNode)) {
            let walker = document.createTreeWalker(
              editorEl,
              NodeFilter.SHOW_TEXT,
              null
            );
            
            let textNode;
            let totalOffset = 0;
            while (textNode = walker.nextNode()) {
              if (textNode === caretPos.offsetNode) {
                insertIndex = totalOffset + caretPos.offset;
                break;
              } else {
                totalOffset += textNode.textContent?.length || 0;
              }
            }
          }
        } else if ((document as any).caretRangeFromPoint) {
          // Fallback for older browsers (with proper typing to avoid deprecation warning)
          const range = (document as any).caretRangeFromPoint(e.clientX, e.clientY);
          if (range && editorEl.contains(range.startContainer)) {
            // Walk through the editor content to find the Quill index
            let walker = document.createTreeWalker(
              editorEl,
              NodeFilter.SHOW_TEXT,
              null
            );
            
            let textNode;
            let totalOffset = 0;
            while (textNode = walker.nextNode()) {
              if (textNode === range.startContainer) {
                insertIndex = totalOffset + range.startOffset;
                break;
              } else {
                totalOffset += textNode.textContent?.length || 0;
              }
            }
          }
        }
        // Manual calculation fallback
        else {
          // Calculate approximate position based on coordinates
          const textLength = editor.getLength();
          const lineHeight = parseInt(getComputedStyle(editorEl).lineHeight) || 20;
          const charWidth = parseInt(getComputedStyle(editorEl).fontSize) * 0.6 || 10; // rough estimate
          
          const lineNumber = Math.floor(y / lineHeight);
          const charPosition = Math.floor(x / charWidth);
          
          // Very rough estimation - this is the weakest fallback
          insertIndex = Math.min(lineNumber * 80 + charPosition, textLength - 1);
        }
        
        // Ensure we don't exceed bounds
        insertIndex = Math.max(0, Math.min(insertIndex, editor.getLength() - 1));
        
      } catch (error) {
        console.log('Position calculation failed, using end position');
        insertIndex = editor.getLength() - 1;
      }
      
      // Insert the placeholder at the calculated position
      editor.insertText(insertIndex, placeholder);
      editor.setSelection(insertIndex + placeholder.length, 0);
    }
  };

  // Prevent default drag over behavior
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  // Fetch enabled fields from backend
  const fetchEnabledFields = async () => {
    if (!session) return;
    
    setFieldsLoading(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/email/entity-field-config`,
        { method: 'GET' },
        session
      );
      
      if (response.ok) {
        const data = await response.json();
        setEnabledFields(data);
      } else {
        console.error('Failed to fetch enabled fields');
      }
    } catch (error) {
      console.error('Error fetching enabled fields:', error);
    } finally {
      setFieldsLoading(false);
    }
  };

  // Generate dummy value based on field name and type
  const generateDummyValue = (fieldName: string, entityType: string): string => {
    const lowerFieldName = fieldName.toLowerCase();
    
    if (lowerFieldName.includes('id')) {
      return `${entityType.toUpperCase()}-123`;
    } else if (lowerFieldName.includes('naam') || lowerFieldName.includes('name')) {
      const names = {
        contact: 'Jan Janssen',
        company: 'Voorbeeld Bedrijf BV',
        building: 'Hoofdkantoor Amsterdam',
        unit: 'Unit 2.05',
        helpdesk: 'Printer defect',
        task: 'Onderhoud uitvoeren',
        contract: 'Servicecontract 2024'
      };
      return names[entityType as keyof typeof names] || `Voorbeeld ${fieldName}`;
    } else if (lowerFieldName.includes('email')) {
      if (entityType === 'contact') {
        return 'jan.janssen@example.be';
      }
      return `info@${entityType}.example.com`;
    } else if (lowerFieldName.includes('telefoon') || lowerFieldName.includes('phone')) {
      return '+32 2 123 45 67';
    } else if (lowerFieldName.includes('adres') || lowerFieldName.includes('address')) {
      return 'Voorbeeldstraat 123, 1000 Brussel';
    } else if (lowerFieldName.includes('datum') || lowerFieldName.includes('date')) {
      return '2024-01-15';
    } else if (lowerFieldName.includes('bedrag') || lowerFieldName.includes('amount') || lowerFieldName.includes('prijs')) {
      return '€ 1.250,00';
    }
    
    return `Voorbeeld ${fieldName}`;
  };

  // Generate available entity types based on enabled fields
  const availableEntityTypes = useMemo(() => {
    const entityTypes = [
      { key: 'contact', label: 'Contact' },
      { key: 'company', label: 'Bedrijf' },
      { key: 'building', label: 'Gebouw' },
      { key: 'unit', label: 'Unit' },
      { key: 'helpdesk', label: 'Helpdesk Ticket' },
      { key: 'task', label: 'Taak' },
      { key: 'contract', label: 'Contract' }
    ];

    return entityTypes.map(entityType => {
      const entityConfig = enabledFields[entityType.key];
      const dummyData: any = {};
      
      if (entityConfig && entityConfig.fields) {
        // Only include enabled fields
        Object.entries(entityConfig.fields).forEach(([fieldName, fieldConfig]: [string, any]) => {
          if (fieldConfig.emailEnabled) {
            const dutchName = fieldConfig.dutchName || fieldName;
            dummyData[dutchName] = generateDummyValue(dutchName, entityType.key);
          }
        });
      }
      
      return {
        ...entityType,
        dummyData,
        hasEnabledFields: Object.keys(dummyData).length > 0
      };
    }).filter(entityType => entityType.hasEnabledFields); // Only show entity types that have enabled fields
  }, [enabledFields]);

  // Handle adding an entity
  const handleAddEntity = (entityTypeKey: string) => {
    const entityType = availableEntityTypes.find(et => et.key === entityTypeKey);
    if (!entityType) return;

    const entityIndex = selectedEntities.filter(e => e.type === entityTypeKey).length + 1;
    const uniqueId = `${entityTypeKey}${entityIndex}`;

    const newEntity = {
      id: uniqueId,
      type: entityTypeKey,
      label: entityType.label,
      uniqueId: uniqueId,
      dummyData: entityType.dummyData
    };

    setSelectedEntities(prev => [...prev, newEntity]);
    setShowAddEntity(false);
  };

  // Handle removing an entity
  const handleRemoveEntity = (entityId: string) => {
    setSelectedEntities(prev => prev.filter(e => e.id !== entityId && e.uniqueId !== entityId));
  };

  // Handle creating a parameter
  const handleCreateParameter = () => {
    if (!newParameterName.trim()) return;
    
    const parameterNumber = parameters.length + 1;
    const sanitizedName = newParameterName.trim().replace(/\s+/g, '_').toLowerCase();
    const prefix = newParameterType === 'global' ? 'gl' : 'ind';
    const newParameter = {
      id: `param${parameterNumber}`,
      name: newParameterName.trim(),
      placeholder: `{${prefix}_${sanitizedName}}`,
      type: newParameterType
    };
    
    setParameters(prev => [...prev, newParameter]);
    setNewParameterName('');
    setShowCreateParameter(false);
  };

  // Handle removing a parameter
  const handleRemoveParameter = (parameterId: string) => {
    setParameters(prev => prev.filter(p => p.id !== parameterId));
  };

  // Function to auto-detect and create entities from template content
  const autoDetectAndCreateEntities = (subject: string, content: string) => {
    const templateContent = subject + ' ' + content;
    const entityPlaceholders = templateContent.match(/\{[a-zA-Z]+\d+\.[a-zA-Z]+\}/g) || [];
    const existingEntityIds = new Set(selectedEntities.map(e => e.uniqueId));
    
    const newEntities: any[] = [];

    entityPlaceholders.forEach(placeholder => {
      // Extract entity type and unique ID from placeholder like {company1.naam}
      const match = placeholder.match(/\{([a-zA-Z]+)(\d+)\./);
      if (match) {
        const [, entityType, entityNumber] = match;
        const uniqueId = `${entityType}${entityNumber}`;
        
        if (!existingEntityIds.has(uniqueId)) {
          // Find the entity type configuration
          const entityTypeConfig = availableEntityTypes.find(et => et.key === entityType);
          
          if (entityTypeConfig) {
            const newEntity = {
              id: uniqueId,
              type: entityType,
              label: entityTypeConfig.label,
              uniqueId: uniqueId,
              dummyData: entityTypeConfig.dummyData
            };
            
            newEntities.push(newEntity);
            existingEntityIds.add(uniqueId);
          }
        }
      }
    });

    if (newEntities.length > 0) {
      setSelectedEntities(prev => [...prev, ...newEntities]);
      console.log(`Auto-detected ${newEntities.length} entities from template:`, newEntities);
    }
    
    return newEntities.length;
  };

  // Function to auto-detect and create parameters from template content
  const autoDetectAndCreateParameters = (subject: string, content: string) => {
    const templateContent = subject + ' ' + content;
    const parameterPlaceholders = templateContent.match(/\{(gl|ind)_[a-zA-Z0-9_]+\}/g) || [];
    const existingPlaceholders = new Set(parameters.map(p => p.placeholder));
    
    const newParameters: Array<{
      id: string;
      name: string;
      placeholder: string;
      type: 'global' | 'individual';
    }> = [];

    parameterPlaceholders.forEach(placeholder => {
      if (!existingPlaceholders.has(placeholder)) {
        // Extract type and name from placeholder
        const match = placeholder.match(/\{(gl|ind)_([a-zA-Z0-9_]+)\}/);
        if (match) {
          const [, prefix, sanitizedName] = match;
          const type = prefix === 'gl' ? 'global' : 'individual';
          const name = sanitizedName.replace(/_/g, ' '); // Convert back to readable format
          
          const newParameter = {
            id: `autodetected_${Date.now()}_${newParameters.length}`,
            name: name,
            placeholder: placeholder,
            type: type as 'global' | 'individual'
          };
          
          newParameters.push(newParameter);
          existingPlaceholders.add(placeholder);
        }
      }
    });

    if (newParameters.length > 0) {
      setParameters(prev => [...prev, ...newParameters]);
      console.log(`Auto-detected ${newParameters.length} parameters from template:`, newParameters);
    }
    
    return newParameters.length;
  };

  // Generate entity dynamic fields
  const getEntityDynamicFields = useMemo(() => {
    const entityFieldGroups: { [key: string]: any } = {};
    
    selectedEntities.forEach(entity => {
      const entityData = entity.dummyData;
      const fields: any[] = [];
      
      Object.entries(entityData).forEach(([fieldName, fieldValue]) => {
        fields.push({
          key: `${entity.uniqueId}.${fieldName}`,
          label: fieldName,
          placeholder: `{${entity.uniqueId}.${fieldName}}`,
          value: fieldValue,
          entityId: entity.uniqueId,
          entityName: entity.label
        });
      });
      
      if (fields.length > 0) {
        entityFieldGroups[entity.uniqueId] = {
          entityName: entity.label,
          entityId: entity.uniqueId,
          fields: fields
        };
      }
    });
    
    return entityFieldGroups;
  }, [selectedEntities]);

  // Track if template is being loaded to prevent duplicate detection
  const [isLoadingTemplate, setIsLoadingTemplate] = useState(false);

  // Fetch enabled fields when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchEnabledFields();
    }
  }, [isOpen, session]);

  useEffect(() => {
    if (template && isOpen) {
      setIsLoadingTemplate(true);
      // Reset parameters and entities first to avoid duplicates
      setParameters([]);
      setSelectedEntities([]);
      setTemplateSubject(template.subject);
      setTemplateContent(template.content);
      setTemplateType(template.templateType);
      setTemplateLabel(template.label);
      
      // Auto-detect entities and parameters when template is loaded
      setTimeout(() => {
        autoDetectAndCreateEntities(template.subject, template.content);
        autoDetectAndCreateParameters(template.subject, template.content);
        setIsLoadingTemplate(false);
      }, 100); // Small delay to ensure state is updated
    }
  }, [template, isOpen]);

  // Auto-detect entities and parameters when content changes (for manual editing only, not during template loading)
  useEffect(() => {
    if (isOpen && (templateSubject || templateContent) && !isLoadingTemplate && template) {
      const detectedEntitiesCount = autoDetectAndCreateEntities(templateSubject, templateContent);
      const detectedParametersCount = autoDetectAndCreateParameters(templateSubject, templateContent);
      if (detectedEntitiesCount > 0) {
        console.log(`Auto-detected ${detectedEntitiesCount} new entities during editing`);
      }
      if (detectedParametersCount > 0) {
        console.log(`Auto-detected ${detectedParametersCount} new parameters during editing`);
      }
    }
  }, [templateSubject, templateContent, isOpen, isLoadingTemplate]);

  const resetForm = () => {
    setTemplateSubject("");
    setTemplateContent("");
    setTemplateType("");
    setTemplateLabel("");
    setSelectedEntities([]);
    setShowAddEntity(false);
    setParameters([]);
    setShowCreateParameter(false);
    setNewParameterName('');
    setNewParameterType('global');
    setIsLoadingTemplate(false);
  };

  const handleEdit = async () => {
    if (!template || !templateSubject.trim() || !templateContent.trim() || !templateType.trim() || !templateLabel.trim()) {
      toast.error("Alle velden zijn verplicht");
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/mail-templates/${template.id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            subject: templateSubject,
            content: templateContent,
            templateType,
            label: templateLabel
          }),
        },
        session
      );

      if (response.ok) {
        toast.success("E-mail sjabloon succesvol bijgewerkt");
        resetForm();
        onClose();
        onSuccess();
      } else {
        toast.error("Fout bij bijwerken van sjabloon");
      }
    } catch (error) {
      console.error("Error updating template:", error);
      toast.error("Fout bij bijwerken van sjabloon");
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={handleClose}
      size="5xl"
      scrollBehavior="inside"
      classNames={{ 
        base: "max-w-[90vw] w-full", 
        body: "p-0" 
      }}
    >
      <ModalContent>
        <ModalHeader>
          <h3 className="text-lg font-semibold">E-mail sjabloon bewerken</h3>
        </ModalHeader>
        <ModalBody className="space-y-6 p-6">
          {/* Basic Template Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Module"
              placeholder={templateTypes.length === 0 ? "Maak eerst een module aan" : "Selecteer een module"}
              selectedKeys={templateType ? [templateType] : []}
              onChange={(e) => setTemplateType(e.target.value)}
              isRequired
              isDisabled={templateTypes.length === 0}
              items={templateTypes.map((type) => ({
                key: type.name,
                label: type.name
              }))}
            >
              {(type) => (
                <SelectItem key={type.key}>
                  {type.label}
                </SelectItem>
              )}
            </Select>
            <Input
              label="Label"
              placeholder="Bijv. Urgent, Standaard, Terugkoppeling"
              value={templateLabel}
              onChange={(e) => setTemplateLabel(e.target.value)}
              isRequired
            />
          </div>

          {/* Subject */}
          <Input
            ref={subjectInputRef}
            label="Onderwerp"
            placeholder="Bijv. Extra foto's nodig - Ticket #{ticketId}"
            value={templateSubject}
            onChange={(e) => setTemplateSubject(e.target.value)}
            onDrop={handleSubjectDrop}
            onDragOver={handleDragOver}
            isRequired
            classNames={{
              inputWrapper: "border-neutral-primary/20 hover:border-moss-primary focus-within:border-moss-primary"
            }}
            description="Sleep dynamische velden hierheen of typ ze handmatig"
          />

          {/* Email Content with Live Preview and Drag Fields */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-neutral-primary">
              Inhoud <span className="text-danger">*</span>
            </label>
            
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Editor */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-neutral-secondary-mid">Editor</span>
                  <span className="text-xs text-neutral-secondary-mid italic">
                    Gebruik {'{Voornaam}'}, {'{Achternaam}'}, {'{Email}'}, etc.
                  </span>
                </div>
                <div 
                  className="border border-neutral-primary/20 rounded-lg overflow-hidden"
                  onDrop={handleEditorDrop}
                  onDragOver={handleDragOver}
                >
                  <ReactQuill
                    ref={quillRef}
                    theme="snow"
                    value={templateContent}
                    onChange={setTemplateContent}
                    modules={modules}
                    placeholder="Typ de inhoud van je sjabloon hier... Of sleep dynamische velden hierheen"
                    style={{ 
                      height: '200px',
                      backgroundColor: 'white'
                    }}
                  />
                </div>
                <div className="h-12"></div>
              </div>
              
              {/* Live Preview */}
              <div className="space-y-2">
                <span className="text-xs text-neutral-secondary-mid">Live Voorbeeld</span>
                <div className="border border-neutral-primary/20 rounded-lg bg-white min-h-[244px] max-h-[244px] overflow-y-auto">
                  <div className="p-4">
                    {templateSubject && (
                      <div className="mb-4 pb-2 border-b border-neutral-primary/10">
                        <span className="text-xs text-neutral-secondary-mid">Onderwerp:</span>
                        <p className="font-medium text-neutral-primary">{replacePreviewDynamicValues(templateSubject, false)}</p>
                      </div>
                    )}
                    
                    <div className="prose prose-sm max-w-none">
                      {templateContent || emailSettings?.headerHtml || emailSettings?.footerHtml ? (
                        <div dangerouslySetInnerHTML={{ 
                          __html: replacePreviewDynamicValues(`
                            ${emailSettings?.headerHtml || ''}
                            ${templateContent || ''}
                            ${emailSettings?.footerHtml || ''}
                          `)
                        }} />
                      ) : (
                        <p className="text-neutral-secondary-mid italic">
                          Typ inhoud om een voorbeeld te zien...
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Drag and Drop Fields Column */}
              <div className="space-y-2">
                <span className="text-xs text-neutral-secondary-mid">Sleep deze velden</span>
                <div className="border border-neutral-primary/20 rounded-lg bg-gray-50 p-4 max-h-[244px] overflow-y-auto">
                  <div className="space-y-4">
                    {/* Contact Fields */}
                    <div className="space-y-2">
                      <h5 className="text-xs font-medium text-neutral-primary flex items-center gap-1">
                        <UsersThree size={12} className="text-blue-600" />
                        Geadresseerde velden
                      </h5>
                      <div className="space-y-2">
                        {dynamicFields.map((field) => (
                          <div
                            key={field.key}
                            draggable
                            onDragStart={(e) => handleDragStart(e, field.placeholder)}
                            className="flex items-center gap-2 p-2 bg-white border border-neutral-primary/20 rounded-sm cursor-grab active:cursor-grabbing hover:border-moss-primary hover:shadow-sm transition-all group text-xs"
                            title={`Sleep naar editor om ${field.placeholder} in te voegen (wordt: ${field.value})`}
                          >
                            <DotsSixVertical size={10} className="text-neutral-secondary-mid group-hover:text-moss-primary" />
                            <div className="grow min-w-0">
                              <code className="text-xs font-mono bg-neutral-100 px-1 rounded-sm text-neutral-primary">
                                {field.placeholder}
                              </code>
                              <div className="text-xs text-neutral-secondary-mid truncate mt-1">
                                = {field.value}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Parameter Fields */}
                    {parameters.length > 0 && (
                      <div className="space-y-2">
                        <h5 className="text-xs font-medium text-neutral-primary flex items-center gap-1">
                          <svg className="w-3 h-3 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                          </svg>
                          Variabele velden
                        </h5>
                        <div className="space-y-2">
                          {parameters.map((parameter) => (
                            <div key={parameter.id} className="flex items-center justify-between">
                              <div
                                draggable
                                onDragStart={(e) => handleDragStart(e, parameter.placeholder)}
                                className="flex items-center gap-2 p-2 bg-white border border-purple-200 rounded-sm cursor-grab active:cursor-grabbing hover:border-purple-400 hover:shadow-sm transition-all group text-xs flex-1 mr-2"
                                title={`Sleep naar editor om ${parameter.placeholder} in te voegen (${parameter.type === 'global' ? 'Globaal' : 'Individueel'})`}
                              >
                                <DotsSixVertical size={10} className="text-purple-600 group-hover:text-purple-700" />
                                <div className="grow min-w-0">
                                  <code className="text-xs font-mono bg-purple-50 px-1 rounded-sm text-purple-700">
                                    {parameter.placeholder}
                                  </code>
                                  <div className="text-xs text-neutral-secondary-mid truncate mt-1">
                                    {parameter.name} ({parameter.type === 'global' ? 'Globaal' : 'Individueel'})
                                  </div>
                                </div>
                              </div>
                              <Button
                                size="sm"
                                variant="light"
                                color="danger"
                                onPress={() => handleRemoveParameter(parameter.id)}
                                className="min-w-0 px-1 h-5 text-xs"
                              >
                                ×
                              </Button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Entity Fields */}
                    {Object.keys(getEntityDynamicFields).length > 0 && (
                      <div className="space-y-2">
                        {Object.entries(getEntityDynamicFields).map(([entityId, entityGroup]: [string, any]) => (
                          <div key={entityId} className="space-y-1">
                            <div className="flex items-center justify-between">
                              <h5 className="text-xs font-medium text-neutral-primary flex items-center gap-1">
                                <Database size={10} className="text-green-600" />
                                {entityGroup.entityName} ({entityId})
                              </h5>
                              <Button
                                size="sm"
                                variant="light"
                                color="danger"
                                onPress={() => handleRemoveEntity(entityId)}
                                className="min-w-0 px-1 h-5 text-xs"
                              >
                                ×
                              </Button>
                            </div>
                            <div className="space-y-1">
                              {entityGroup.fields.map((field: any) => (
                                <div
                                  key={field.key}
                                  draggable
                                  onDragStart={(e) => handleDragStart(e, field.placeholder)}
                                  className="flex items-center gap-2 p-2 bg-white border border-green-200 rounded-sm cursor-grab active:cursor-grabbing hover:border-green-400 hover:shadow-sm transition-all group text-xs"
                                  title={`Sleep naar editor om ${field.placeholder} in te voegen (wordt: ${field.value})`}
                                >
                                  <DotsSixVertical size={10} className="text-green-600 group-hover:text-green-700" />
                                  <div className="grow min-w-0">
                                    <code className="text-xs font-mono bg-green-50 px-1 rounded-sm text-green-700">
                                      {field.placeholder}
                                    </code>
                                    <div className="text-xs text-neutral-secondary-mid truncate mt-1">
                                      = {field.value}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Add Entity Section */}
          <div className="mt-6 space-y-4 border-t border-neutral-primary/10 pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Database size={20} className="text-neutral-secondary-mid" />
                <h4 className="text-lg font-medium text-neutral-primary">Gegevens toevoegen</h4>
              </div>
              <Button
                variant="bordered"
                size="sm"
                startContent={<Plus size={16} />}
                onPress={() => setShowAddEntity(!showAddEntity)}
                className="border-moss-primary text-moss-primary hover:bg-moss-primary hover:text-white"
              >
                Gegevens toevoegen
              </Button>
            </div>
            
            {showAddEntity && (
              <div className="space-y-3">
                {fieldsLoading ? (
                  <div className="flex items-center justify-center py-4">
                    <div className="flex items-center gap-2 text-neutral-secondary-mid">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-moss-primary"></div>
                      <span className="text-sm">Laden van beschikbare velden...</span>
                    </div>
                  </div>
                ) : availableEntityTypes.length > 0 ? (
                  <>
                    <div className="grid grid-cols-2 gap-2">
                      {availableEntityTypes.map((entityType) => (
                        <Button
                          key={entityType.key}
                          variant="bordered"
                          className="justify-start"
                          startContent={<Database size={16} />}
                          onPress={() => handleAddEntity(entityType.key)}
                        >
                          {entityType.label}
                        </Button>
                      ))}
                    </div>
                    <p className="text-xs text-neutral-secondary-mid">
                      Klik op een gegevenstype om het toe te voegen met alleen de ingeschakelde velden voor je sjabloon.
                    </p>
                  </>
                ) : (
                  <div className="text-center py-4">
                    <p className="text-sm text-neutral-secondary-mid">
                      Geen gegevenstypes beschikbaar met ingeschakelde velden.
                    </p>
                    <p className="text-xs text-neutral-secondary-mid mt-1">
                      Ga naar de E-mail Veld Instellingen om velden in te schakelen.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Auto-detection Info for Entities */}
            {selectedEntities.some(e => e.id === e.uniqueId) && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <div className="shrink-0">
                    <svg className="w-4 h-4 text-green-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="grow">
                    <h4 className="font-medium text-green-900 mb-1">Entiteiten automatisch gedetecteerd</h4>
                    <p className="text-sm text-green-800">
                      We hebben entiteiten gedetecteerd in deze sjabloon en automatisch toegevoegd met dummy gegevens voor de preview. 
                      Deze zijn gemarkeerd als "Auto-gedetecteerd".
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Show selected entities */}
            {selectedEntities.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm text-neutral-secondary-mid">
                  Toegevoegde gegevens (gebruik deze in uw sjabloon als dynamische velden):
                </p>
                <div className="grid grid-cols-1 gap-2">
                  {selectedEntities.map((entity) => {
                    const isAutoDetected = entity.id === entity.uniqueId; // Auto-detected entities have id same as uniqueId
                    return (
                      <div
                        key={entity.uniqueId}
                        className={`flex items-center justify-between rounded-lg p-3 border ${isAutoDetected ? 'bg-blue-50 border-blue-200' : 'bg-neutral-primaryLight/5 border-neutral-primary/10'}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="flex items-center gap-2">
                            <Database size={16} className={isAutoDetected ? "text-blue-600" : "text-moss-primary"} />
                            <span className="font-medium text-neutral-primary">{entity.label}</span>
                            <span className={`text-xs px-2 py-1 rounded-full ${isAutoDetected ? 'bg-blue-100 text-blue-700' : 'bg-moss-primary/10 text-moss-primary'}`}>
                              {entity.uniqueId}
                            </span>
                            {isAutoDetected && (
                              <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-700 font-medium">
                                Auto-gedetecteerd
                              </span>
                            )}
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant="light"
                          color="danger"
                          onPress={() => handleRemoveEntity(entity.uniqueId)}
                          className="min-w-0 px-2"
                        >
                          ×
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Parameter Creation Section */}
          <div className="mt-6 space-y-4 border-t border-neutral-primary/10 pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-neutral-secondary-mid" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                </svg>
                <h4 className="text-lg font-medium text-neutral-primary">Variabele velden aanmaken</h4>
              </div>
              <Button
                variant="bordered"
                size="sm"
                startContent={<Plus size={16} />}
                onPress={() => setShowCreateParameter(!showCreateParameter)}
                className="border-purple-500 text-purple-600 hover:bg-purple-500 hover:text-white"
              >
                Variabel veld toevoegen
              </Button>
            </div>
            
            {showCreateParameter && (
              <div className="space-y-4 bg-purple-50 border border-purple-200 rounded-lg p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-neutral-primary">
                      Variabel veld naam
                    </label>
                    <Input
                      value={newParameterName}
                      onChange={(e) => setNewParameterName(e.target.value)}
                      placeholder="Bijv. Deadline, Locatie, Bedrag"
                      size="sm"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-neutral-primary">
                      Type
                    </label>
                    <Select
                      selectedKeys={[newParameterType]}
                      onChange={(e) => setNewParameterType(e.target.value as 'global' | 'individual')}
                      items={[
                        { key: "global", label: "Globaal (zelfde waarde voor iedereen)" },
                        { key: "individual", label: "Individueel (unieke waarde per ontvanger)" }
                      ]}
                      size="sm"
                    >
                      {(item) => (
                        <SelectItem key={item.key}>
                          {item.label}
                        </SelectItem>
                      )}
                    </Select>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    className="bg-purple-600 text-white"
                    onPress={handleCreateParameter}
                    isDisabled={!newParameterName.trim()}
                  >
                    Variabel veld aanmaken
                  </Button>
                  <Button
                    size="sm"
                    variant="light"
                    onPress={() => {
                      setShowCreateParameter(false);
                      setNewParameterName('');
                    }}
                  >
                    Annuleren
                  </Button>
                </div>
              </div>
            )}

            {/* Auto-detection Info */}
            {parameters.some(p => p.id.startsWith('autodetected_')) && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <div className="shrink-0">
                    <svg className="w-4 h-4 text-green-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="grow">
                    <h4 className="font-medium text-green-900 mb-1">Parameters automatisch gedetecteerd</h4>
                    <p className="text-sm text-green-800">
                      We hebben variabele velden gedetecteerd in deze sjabloon en automatisch toegevoegd. 
                      Deze zijn gemarkeerd als "Auto-gedetecteerd" en kunnen nu gebruikt worden in de preview.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Show created parameters */}
            {parameters.length > 0 && (
              <div className="space-y-2">
                <h5 className="text-sm font-medium text-neutral-primary">Aangemaakte variabele velden:</h5>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {parameters.map((parameter) => {
                    const isAutoDetected = parameter.id.startsWith('autodetected_');
                    return (
                      <div key={parameter.id} className={`flex items-center justify-between p-2 rounded-sm ${isAutoDetected ? 'bg-blue-50 border border-blue-200' : 'bg-purple-50 border border-purple-200'}`}>
                        <div className="flex items-center gap-2">
                          <code className={`text-xs font-mono px-1 rounded-sm ${isAutoDetected ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                            {parameter.placeholder}
                          </code>
                          <span className="text-sm text-neutral-primary">{parameter.name}</span>
                          <span className={`text-xs px-2 py-1 rounded-full ${isAutoDetected ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                            {parameter.type === 'global' ? 'Globaal' : 'Individueel'}
                          </span>
                          {isAutoDetected && (
                            <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-700 font-medium">
                              Auto-gedetecteerd
                            </span>
                          )}
                        </div>
                        <Button
                          size="sm"
                          variant="light"
                          color="danger"
                          onPress={() => handleRemoveParameter(parameter.id)}
                          className="min-w-0 px-2"
                        >
                          ×
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Information Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="shrink-0">
                <div className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center">
                  <svg className="w-3 h-3 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
              <div className="grow">
                <h4 className="font-medium text-blue-900 mb-2">Dynamische gegevens gebruiken</h4>
                <div className="text-sm text-blue-800 space-y-2">
                  <p>
                    <strong>Sleep de velden</strong> naar het onderwerp of de inhoud om automatisch contactgegevens in te vullen. 
                    Je kunt ook handmatig typen zoals <code className="bg-blue-100 px-1 rounded-sm text-blue-900">{'{Voornaam}'}</code>.
                  </p>
                  <p>
                    <strong>Gegevens velden:</strong> Voeg gegevens toe om hun velden als dynamische data te gebruiken 
                    (bijv. <code className="bg-blue-100 px-1 rounded-sm text-blue-900">{'{company1.Name}'}</code>).
                  </p>
                  <p>
                    <strong>Variabele velden:</strong> Maak aangepaste variabele velden aan die <span className="bg-purple-100 px-1 rounded-sm text-purple-700 font-medium">paars gemarkeerd</span> worden. 
                    Globale variabele velden hebben dezelfde waarde voor iedereen, individuele variabele velden zijn uniek per ontvanger.
                  </p>
                  <p>
                    Het <strong>live voorbeeld</strong> toont hoe je sjabloon eruit ziet met voorbeeldgegevens ingevuld.
                    Geadresseerde velden worden <span className="bg-yellow-100 px-1 rounded-sm text-yellow-800 font-medium">geel gemarkeerd</span>, 
                    gegevens velden <span className="bg-green-100 px-1 rounded-sm text-green-800 font-medium">groen gemarkeerd</span>, en 
                    variabele velden <span className="bg-purple-100 px-1 rounded-sm text-purple-700 font-medium">paars gemarkeerd</span>.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="light" onPress={handleClose}>
            Annuleren
          </Button>
          <Button 
            className="bg-moss-primary text-white" 
            onPress={handleEdit}
            isLoading={isLoading}
          >
            Sjabloon bijwerken
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}; 